'use client'

import { type ReactNode, useEffect } from 'react'
import { useAuth } from '@/composable/useAuth'
import { useStore } from '@/store/store'
import { getUserData } from '@/utils/functions'
import type { IExtendedUser } from '@shared/types/base'
import { getPlanDataInstitution } from '@/generated/dataconnect'
import type { IPlanInstitucion } from '@/types/base'
import { getAlumniDataConnect } from '@/lib/dataconnect'
import { ETipoPlanInstitucion } from '@/types/enums'

/**
 * Con el store solo en memoria (sin persist), al recargar con sesión Firebase activa
 * hay que volver a cargar el perfil RTDB en Zustand (equivalente a hidratar en login).
 */
export function AlumniStoreBootstrap({ children }: Readonly<{ children: ReactNode }>) {
	const { user: firebaseUser, loading } = useAuth()
	const storeUser = useStore(s => s.user)
	const setUser = useStore(s => s.setUser)
	const setUserAuth = useStore(s => s.setUserAuth)
	const setCurrentInstitutionId = useStore(s => s.setCurrentInstitutionId)
	const setPlanInstitucion = useStore(s => s.setPlanInstitucion)

	useEffect(() => {
		if (loading) return
		if (!firebaseUser) return
		if (storeUser != null) return

		let cancelled = false
		;(async () => {
			const userData = await getUserData(firebaseUser.uid)
			if (cancelled) return
			setUser(userData as IExtendedUser)
			setUserAuth(firebaseUser)
			const instId = userData.userData?.institution
			if (instId != null && !Number.isNaN(Number(instId))) {
				setCurrentInstitutionId(String(instId))
			}
			const dc = getAlumniDataConnect()
			const { data } = await getPlanDataInstitution(dc, { idInstitucion: String(instId) || '' })
			if (data.planInstitucion) {
				setPlanInstitucion(data.planInstitucion as IPlanInstitucion)
			} else {
				setPlanInstitucion({ idInstitucion: instId || '', precio: 0, tipo: ETipoPlanInstitucion.BASICO, iva: 0, fechaInicioContrato: '', fechaFinContrato: '' } as IPlanInstitucion)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [loading, firebaseUser, storeUser, setUser, setUserAuth, setCurrentInstitutionId])

	return <>{children}</>
}
